import { msg } from '@lingui/macro'

import * as React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { LinguiMessageDescriptor, useTrans } from '~logic/i18n/useTrans'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { InputErrorMessage } from '~ui/components/formElements/accessories/InputMessages'

import { PasswordInputElement } from './PasswordInputElement'

type TPasswordForm = { password: string }

type TProps = NoChildren & {
  label?: LinguiMessageDescriptor
  disableAutocomplete?: boolean
}

/**
 * Password input used when the customer enters they current password.
 * Can be used both in the login and the password change.
 *
 * Different labels can be used via the `label` prop, defaults to _Password_ (translated).
 * Autocomplete can be turned off by the `disableAutocomplete` prop.
 *
 * ! Needs the `FormProvider` from 'react-hook-form' to be rendered somewhere above,
 * see {@link https://react-hook-form.com/api/formprovider} for examples and details.
 */
export const PasswordInput: React.FC<TProps> = ({
  disableAutocomplete = false,
  label = msg`Password`,
}) => {
  const { trans } = useTrans()
  const { control, trigger } = useFormContext<TPasswordForm>()

  return (
    <InputControl isRequired>
      <InputLabel>{trans(label)}</InputLabel>
      <Controller
        name={`password`}
        control={control}
        rules={{
          required: { value: true, message: trans(msg`Fill the password`) },
          onChange: () => trigger(`password`),
        }}
        render={({
          field: { onChange, onBlur, value, name },
          fieldState: { invalid, error },
          formState: { isSubmitting },
        }) => (
          <>
            <PasswordInputElement
              id={name}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              isInvalid={invalid}
              isDisabled={isSubmitting}
              autoComplete={disableAutocomplete ? `off` : `current-password`}
            />
            <AnimatedHeight isOpen={invalid && Boolean(error?.message)}>
              <InputErrorMessage>{error?.message ?? null}</InputErrorMessage>
            </AnimatedHeight>
          </>
        )}
      />
    </InputControl>
  )
}
